// @ts-nocheck
/*
 * Port of v1 views/dashboard/components/CallQualityMetricsPanel.js (simple view).
 * Used by the Call Quality report page AND the Agent Call Stats dashboard.
 *
 * APIs unchanged: API_CALL_QUALITY_METRICS (all agents) OR
 * API_CALL_QUALITY_AGENT_METRICS(id). Every metric is still returned and
 * stored — this only narrows what is put on screen for an end customer:
 *   - verdict banner + 5 tiles (Calls with problems + the four simple ones)
 *   - per-agent table cut from 15 columns to 4
 *   - worst-calls table cut from 11 columns to 5
 *   - both charts removed
 */
import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { callRowCustomerNumber } from '@/features/cti/ctiHelpers';
import {
  API_CALL_QUALITY_METRICS,
  API_CALL_QUALITY_AGENT_METRICS,
} from '@/service/url';
import {
  WARNING_TEXT,
  buildSimpleMetrics,
  buildSimpleVerdict,
  mosQuality,
} from '@/utilities/callQualityRating';
import {
  QualityTile,
  QualityVerdict,
} from '@/components/partials/components/CallQualitySimple';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/data-table/DataTable';

const n = (v: any) => {
  if (v === null || v === undefined || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};

/** Quality as a coloured word — no MOS number in the customer-facing view. */
const qualityCell = (row: any) => {
  const q = mosQuality(row.mos_avg);
  return <span style={{ color: q.color, fontWeight: 700 }}>{q.label}</span>;
};

const firstIssue = (row: any) => {
  const codes = Array.isArray(row.warnings)
    ? row.warnings.filter((c: any) => WARNING_TEXT[c])
    : [];
  return codes.length ? WARNING_TEXT[codes[0]] : '—';
};

function CallQualityMetricsPanel({ agentId = null, from = null, to = null, title }: any) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    const params: string[] = [];
    if (from) params.push(`from=${encodeURIComponent(from)}`);
    if (to) params.push(`to=${encodeURIComponent(to)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    const url = agentId
      ? `${API_CALL_QUALITY_AGENT_METRICS(agentId)}${qs}`
      : `${API_CALL_QUALITY_METRICS}${qs}`;

    getApiCall(url)
      .then((resp: any) => {
        if (!mounted) return;
        setData(resp || null);
      })
      .catch((e: any) => {
        if (!mounted) return;
        setData(null);
        setError(typeof e === 'string' ? e : 'Failed to load call quality metrics');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [agentId, from, to]);

  const summary = data?.summary || null;
  const agents = useMemo(() => (Array.isArray(data?.agents) ? data.agents : []), [data]);
  const worstCalls = useMemo(
    () => (Array.isArray(data?.worst_calls) ? data.worst_calls : []),
    [data],
  );

  const calls = n(summary?.calls) || 0;
  const poorCalls = n(summary?.poor_calls) || 0;
  const poorPct = calls > 0 ? (poorCalls / calls) * 100 : null;
  const verdict = useMemo(() => buildSimpleVerdict(summary), [summary]);

  const agentColumns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'agent_name', header: 'Agent' },
      { accessorKey: 'calls', header: 'Calls' },
      {
        id: 'quality',
        header: 'Quality',
        accessorFn: (row: any) => n(row.mos_avg) ?? -1,
        cell: ({ row }: any) => qualityCell(row.original),
      },
      {
        id: 'poor_calls',
        header: 'Calls with problems',
        accessorFn: (row: any) => n(row.poor_calls) || 0,
      },
    ],
    [],
  );

  const worstColumns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: 'started',
        header: 'Started',
        accessorFn: (row: any) => row.started_at || '',
        cell: ({ row }: any) =>
          row.original.started_at
            ? new Date(row.original.started_at).toLocaleString()
            : '—',
      },
      {
        id: 'agent',
        header: 'Agent',
        accessorFn: (row: any) => row.agent?.agent_name || '',
        cell: ({ row }: any) => row.original.agent?.agent_name || '—',
      },
      {
        id: 'number',
        header: 'Number',
        accessorFn: (row: any) => callRowCustomerNumber(row),
        cell: ({ row }: any) => callRowCustomerNumber(row.original) || '—',
      },
      {
        id: 'quality',
        header: 'Quality',
        accessorFn: (row: any) => n(row.mos_avg) ?? -1,
        cell: ({ row }: any) => qualityCell(row.original),
      },
      {
        id: 'issue',
        header: 'What went wrong',
        accessorFn: (row: any) => firstIssue(row),
      },
    ],
    [],
  );

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h5 className="m-0 text-base font-semibold">{title || 'Call Quality'}</h5>
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          {calls} measured call{calls === 1 ? '' : 's'}
        </span>
      </div>

      {error ? <div className="mb-2 text-sm text-destructive">{error}</div> : null}

      {loading ? (
        <div className="py-4 text-center text-sm text-muted-foreground">
          Loading call quality…
        </div>
      ) : calls === 0 ? (
        <div className="py-4 text-center text-sm text-muted-foreground">
          No call quality data for this range. Quality is measured only on calls made
          from the browser softphone.
        </div>
      ) : (
        <>
          <QualityVerdict verdict={verdict} style={{ marginBottom: 12 }} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <QualityTile
              label="Calls with problems"
              value={`${poorCalls}`}
              sub={poorPct === null ? '' : `${poorPct.toFixed(1)}% of ${calls} calls`}
              desc="Calls that did not sound good enough for the person on the other end."
              color={
                poorPct !== null && poorPct > 15
                  ? '#ef4444'
                  : poorPct > 5
                    ? '#f59e0b'
                    : '#10b981'
              }
              status={poorPct !== null && poorPct <= 5 ? 'good' : 'warn'}
            />
            {buildSimpleMetrics(summary).map((m: any) => (
              <QualityTile key={m.key} {...m} />
            ))}
          </div>

          {!agentId && agents.length > 0 ? (
            <div style={{ width: '100%', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, marginBottom: 4 }}>
                QUALITY BY AGENT
              </div>
              <DataTable
                tableId="call-quality.agents"
                columns={agentColumns}
                data={agents}
                pageSize={10}
                emptyTitle="No agent quality data"
                disableExport
              />
            </div>
          ) : null}

          {worstCalls.length > 0 ? (
            <div style={{ width: '100%' }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, marginBottom: 4 }}>
                CALLS THAT DID NOT SOUND GOOD
              </div>
              <DataTable
                tableId="call-quality.worst"
                columns={worstColumns}
                data={worstCalls}
                pageSize={10}
                emptyTitle="No calls"
                disableExport
              />
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

export default CallQualityMetricsPanel;
